import Action from 'ageas-react-component-action'
import MainLoader from 'ageas-react-component-loader'
import { useEffect, useState } from 'react'

import { Attachment } from '../../../@types/RequestQuotation'
import { useDocumentStore } from '../../../Stores/useDocumentStore'
import { useQuotationMessageUpdateStore } from '../../../Stores/useQuotationMessageUpdateStore'
import { CONTEXT_DOCUMENT } from '../../../constants/contextDocument'
import { useGetQuotation } from '../../../hooks/api/getQuotation/useGetQuotation'
import { useGetQuotationPayload } from '../../../hooks/api/getQuotation/useGetQuotationPayload'
import { useUpdateQuotation } from '../../../hooks/api/updateQuotation/useUpdateQuotation'
import { useUpdateQuotationPayload } from '../../../hooks/api/updateQuotation/useUpdateQuotationPayload'
import { useUploadDocument } from '../../../hooks/api/uploadDocuments/useUploadDocument'
import { useUploadDocumentPayload } from '../../../hooks/api/uploadDocuments/useUploadDocumentPayload'
import { t } from '../../../i18n'
import {
  AddAnimalButtonContainer,
  AddAnimalButtonText,
} from '../../AnimalInsuranceTable/styles'
import { Plus } from '../../Icons'
import { RowAttachment } from './RowAttachment'
import { RowAttachmentReadOnly } from './RowAttachmentReadOnly'

export type AttachmentType = {
  id: number
} & Attachment

export type FileProps = {
  id: number
  name: string
  path?: string
  type?: string
  preview?: string
  content?: string
}

type Props = {
  context: string
  showUpdateQuotationButton?: boolean
  isLoadingUpdateQuotation?: boolean
  data: AttachmentType[]
  disabled?: boolean
}

export const TableAttachment = ({
  context,
  showUpdateQuotationButton,
  isLoadingUpdateQuotation,
  data,
  disabled,
}: Props) => {
  const [addFile, setAddFile] = useState((data || [])?.length === 0)
  const {
    mutate: mutateUpdateQuotation,
    isLoading,
    isSuccess,
  } = useUpdateQuotation()
  const { mutate: mutateGetQuotation } = useGetQuotation()
  const {
    mutate: mutateUploadDocuments,
    isLoading: isLoadingUploadDocuments,
    isSuccess: isSuccessUploadDocuments,
  } = useUploadDocument()
  const { updateQuotationPayload } = useUpdateQuotationPayload()
  const getQuotationPayload = useGetQuotationPayload()
  const { uploadDocumentPayload } = useUploadDocumentPayload()
  const { resetQuotationMessage, quotationUpdate } =
    useQuotationMessageUpdateStore()
  const { resetDocuments, resetInitialDocumentsState } = useDocumentStore()

  const disabledSendUpdateQuotationButton =
    (quotationUpdate?.message?.messageText || '')?.length === 0 &&
    (quotationUpdate?.attachments || [])?.length === 0

  const handleAddRow = () => {
    setAddFile(true)
  }

  const formattedDataToRow = (data: AttachmentType[]) => {
    return (data || [])?.map((item) => ({
      id: item.id,
      name: item.documentName,
      type: item.documentType,
    }))
  }

  useEffect(() => {
    if (isSuccess) {
      mutateGetQuotation(getQuotationPayload)
      resetQuotationMessage()
      resetDocuments()
    }
  }, [isSuccess])

  useEffect(() => {
    if (isSuccessUploadDocuments) {
      resetInitialDocumentsState()
    }
  }, [isSuccessUploadDocuments])

  return (
    <>
      <MainLoader show={isLoading} />
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th></th>
              <th>{t('pages.step7.attachmentDocuments.file')}</th>
              <th className="td-right">
                {t('pages.step7.attachmentDocuments.typeFile')}
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {formattedDataToRow(data)?.map((item) => (
              <RowAttachmentReadOnly
                item={item as FileProps}
                key={item.id}
                context={context}
              />
            ))}
            {addFile && (
              <RowAttachment
                item={{
                  id: Number((data || [])?.at(-1)?.id) + 1 || 1,
                  name: '',
                  type: '',
                }}
                context={context}
                onCallback={() => setAddFile(false)}
              />
            )}
          </tbody>
        </table>
      </div>
      <AddAnimalButtonContainer>
        <div className="button" onClick={handleAddRow}>
          <Plus color="#333" size={24} />
          <AddAnimalButtonText>
            {t('pages.step7.attachmentDocuments.addFile')}
          </AddAnimalButtonText>
        </div>
      </AddAnimalButtonContainer>
      {context === CONTEXT_DOCUMENT.QUOTATION_UPDATE &&
        showUpdateQuotationButton && (
          <div className="button-group">
            <Action
              id="buttonSendUpdateQuotation"
              label={t('pages.step5.sendUpdateQuotationButton')}
              type="button"
              disable={
                isLoadingUpdateQuotation ||
                disabledSendUpdateQuotationButton ||
                disabled
              }
              onValueChanged={() =>
                mutateUpdateQuotation(updateQuotationPayload(context))
              }
            />
          </div>
        )}
      {context === CONTEXT_DOCUMENT.CONFIRMATION_UPLOAD && (
        <div className="button-group">
          <Action
            id="buttonConfirmationDocumentUpload"
            label={t('pages.step7.sendDocuments')}
            type="button"
            disable={isLoadingUploadDocuments || disabled || data.length === 0}
            onValueChanged={() => {
              mutateUploadDocuments(uploadDocumentPayload(context))
            }}
          />
        </div>
      )}
    </>
  )
}
